import {
  Content,
  Header,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { Grid } from '@material-ui/core';
import { SupportHeader } from '../supportHeader/SupportHeader';
import type { IMessage } from '../../schemas/IMessage.interface';
import { AboutSection } from './aboutSection/AboutSection';
import { ExamplesSection } from './examplesSection/ExamplesSection';
import { SchemaSection } from './schemaSection/SchemaSection';
import { IAsyncApiSpecification } from '@internal/schemas-common';
import { useStyles } from './messageDetail.style';

interface MessageDetailProps {
  messageName: string;
  messages: IMessage[];
  isLoading: boolean;
  error: Error | null;
}

function MessageDetail({ messageName, messages, isLoading, error }: MessageDetailProps) {
  const { cx, classes } = useStyles();
  const message = messages.find(lookupMessage => lookupMessage.name === messageName);

  const displayName = message?.name.split('.').slice(1).join('.') || messageName;
  const typeLabel = `MESSAGES - ${message?.type?.toUpperCase() || 'MESSAGE'}`;

  const sectionKeys = ['payload', 'headers'] as const;

  function createSpecification<S extends typeof sectionKeys[number]>(
    section: S,
    data: IMessage[S] | null
  ): IAsyncApiSpecification['specification'] | null {
    if (!data) return null;
    return {
      asyncapi: '3.0.0',
      info: { title: messageName, version: '1.0.0' },
      components: {
        messages: {
          [messageName]: {
            name: messageName,
            [section]: data,
          },
        },
      },
    };
  }

  const specifications = message
    ? Object.fromEntries(
        sectionKeys.map(key => [
          key,
          message[key]
            ? createSpecification(key, message[key])
            : null,
        ])
      )
    : null;

  const renderContent = () => {
    if (error) {
      return <ResponseErrorPanel error={error} />;
    }

    if (isLoading) {
      return <Progress />;
    }

    if (!message) {
      return <ResponseErrorPanel error={new Error('Message not found.')} />;
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} className={cx(classes.gridItem)}>
          <AboutSection message={message} />
        </Grid>

        <Grid item xs={12} md={6} className={cx(classes.gridItem)}>
          <ExamplesSection examples={message.examples} />
        </Grid>

        <Grid item xs={12} md={6} className={cx(classes.gridItem)}>
          <SchemaSection titleOfComponent="Payload" specification={specifications?.payload ?? null} />
        </Grid>

        <Grid item xs={12} md={6} className={cx(classes.gridItem)}>
          <SchemaSection titleOfComponent="Headers" specification={specifications?.headers ?? null} />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Header title={displayName} type={typeLabel} />
      <Content>
        <SupportHeader />
        {renderContent()}
      </Content>
    </>
  );
}

export { MessageDetail };
